import dayjs from 'src/helpers/dayjs';
import { IEdition } from 'src/interfaces/Edition';
import {
  IVisibilityFilter,
  IVisibilityRule,
  VisibilityPredicate,
} from 'src/interfaces/PageBlock';

export interface VisibilityContext {
  currentEdition?: IEdition;
  activeEditionId?: string;
  userLoggedIn?: boolean;
}

export function evaluatePredicate(
  predicate: VisibilityPredicate,
  ctx: VisibilityContext,
): boolean {
  const { currentEdition, userLoggedIn } = ctx;

  switch (predicate) {
    case 'activeEdition':
      return Boolean(currentEdition);
    case 'beforeSignupDeadline':
      return currentEdition
        ? dayjs().isBefore(dayjs(currentEdition.signupDeadline))
        : false;
    case 'afterSignupDeadline':
      return currentEdition
        ? !dayjs().isBefore(dayjs(currentEdition.signupDeadline))
        : false;
    case 'isSignupOpen':
      return Boolean(currentEdition?.isSignupOpen);
    case 'canShowResults':
      return Boolean(currentEdition?.canShowResults);
    case 'userLoggedIn':
      return Boolean(userLoggedIn);
    default:
      return false;
  }
}

function evaluateRule(rule: IVisibilityRule, ctx: VisibilityContext): boolean {
  const actual = evaluatePredicate(rule.predicate, ctx);
  return actual === rule.expected;
}

export function evaluateVisibilityFilter(
  filter: IVisibilityFilter | undefined,
  ctx: VisibilityContext,
): boolean {
  if (!filter?.rules?.length) {
    return true;
  }

  const [first, ...rest] = filter.rules;
  if (!first) {
    return true;
  }
  let result = evaluateRule(first, ctx);

  rest.forEach((rule, index) => {
    const connector = filter.connectors[index] ?? 'and';
    const next = evaluateRule(rule, ctx);
    result = connector === 'or' ? result || next : result && next;
  });

  return result;
}

type SignupOpenEditionFields = Pick<IEdition, 'signupDeadline' | 'isSignupOpen'>;

/** True when the edition signup window is open (before deadline and admin flag on). */
export function isSignupOpenForEdition(edition: SignupOpenEditionFields | undefined): boolean {
  if (!edition) return false;
  return (
    dayjs().isBefore(dayjs(edition.signupDeadline)) && Boolean(edition.isSignupOpen)
  );
}

export function shouldShowResultsBody(
  edition: IEdition,
  activeEditionId: string | undefined,
): boolean {
  if (edition.id !== activeEditionId) {
    return Boolean(edition.statistics);
  }
  return Boolean(edition.canShowResults && edition.statistics);
}
